import apiClient from "../../../services/apiClient";
import API_ENDPOINTS from "../../../config/apiEndpoints";
import utils from "../../../utils/utils";
import { fetchRoleLookup, fetchSiteLookup } from "../../../services/lookupService";
import {
  enrichDocumentsWithPlaceholderLinks,
  fetchDashboardPlaceholderStats,
  fetchReadinessReport,
  fetchStudyPlaceholders,
  fetchStudyPlaceholdersForGates,
  filterReadinessReportByGate,
  consolidateDocumentSignatureRows,
  stripGuid,
} from "./isfPlaceholderService";
import { buildDashboardTaskQueue } from "./isfSiteOverviewService";
import { normalizeStudyFolderResponseData } from "./isfSiteSetupService";

export const ALL_STUDIES_OPTION = { label: "All studies", value: "" };
export const DEFAULT_CATEGORY = { label: "eISF", value: "eISF" };

const BINDER_STUDY_CACHE_VERSION = "cro-grants-v3";

export function parseStudyList(response) {
  const data = response?.data ?? response;
  let rows = [];
  if (Array.isArray(data)) {
    rows = data;
  } else if (Array.isArray(data?.StudyList)) {
    rows = data.StudyList;
  } else if (Array.isArray(data?.Table)) {
    rows = data.Table;
  } else {
    const tables = data?.Tables || data?.tables;
    if (Array.isArray(tables)) {
      const studyTable =
        tables.find((t) => {
          const name = String(t?.Name || t?.TableName || "").toLowerCase();
          return name === "studylist" || name.includes("study");
        }) || tables.find((t) => Array.isArray(t?.Rows) && t.Rows[0]?.StudyId != null);
      if (Array.isArray(studyTable?.Rows)) {
        rows = studyTable.Rows;
      }
    }
  }
  return rows.filter((row) => row != null && row.StudyId != null && row.StudyId !== "");
}

async function fetchEisfStudyListFast({ organizationId, createdBy }) {
  const response = await apiClient.post(API_ENDPOINTS.ISF.PLACEHOLDER_ACTIVITY, {
    ActionType: "GetEISfStudyList",
    OrganizationIdRef: organizationId,
    CreatedBy: createdBy,
  });
  return parseStudyList(response);
}

async function fetchEisfStudyListLegacy({ organizationId, createdBy }) {
  const response = await apiClient.post(API_ENDPOINTS.ISF.GETSTUDYLIST, {
    OrganizationId: organizationId,
    Action: "GetEISfStudies",
    CreatedBy: createdBy,
  });
  return parseStudyList(response);
}

/** Fast binder picker — all assigned studies + HaseISFTemplate / HasePSFTemplate flags. */
async function fetchEisfStudyListForBinder({ organizationId, createdBy }) {
  const response = await apiClient.post(API_ENDPOINTS.ISF.PLACEHOLDER_ACTIVITY, {
    ActionType: "GetEISfStudyListForBinder",
    OrganizationIdRef: organizationId,
    CreatedBy: createdBy,
  });
  return parseStudyList(response);
}

export function getCachedEisfStudies(organizationId, createdBy) {
  const key = `${organizationId}:${createdBy}`;
  const cached = studyListCache.get(key);
  if (cached?.studies?.length) {
    return { studies: cached.studies, options: cached.options };
  }
  return null;
}

const studyListCache = new Map();
const binderStudyListCache = new Map();

/** Full study rows (binder flags, PI, etc.) — cached for ISF study picker. */
export async function fetchEisfStudiesForBinder({
  organizationId,
  createdBy,
  force = false,
}) {
  const key = `${BINDER_STUDY_CACHE_VERSION}:${organizationId}:${createdBy}`;
  const cached = binderStudyListCache.get(key);

  if (!force && cached?.studies?.length) {
    return { studies: cached.studies, options: cached.options };
  }
  if (!force && cached?.promise) {
    return cached.promise;
  }

  const promise = (async () => {
    const [fastResult, legacyResult] = await Promise.allSettled([
      fetchEisfStudyListForBinder({ organizationId, createdBy }),
      fetchEisfStudyListLegacy({ organizationId, createdBy }),
    ]);
    const fastList =
      fastResult.status === "fulfilled" ? fastResult.value : [];
    const legacyList =
      legacyResult.status === "fulfilled" ? legacyResult.value : [];
    if (legacyResult.status === "rejected") {
      console.warn("Legacy GetEISfStudies failed.", legacyResult.reason);
    }
    const list =
      legacyList.length > fastList.length || !fastList.length
        ? legacyList
        : fastList;
    const options = utils.mapToOptions(list, "StudyName", "StudyId");
    const result = { studies: list, options };
    binderStudyListCache.set(key, { studies: list, options });
    return result;
  })();

  binderStudyListCache.set(key, { ...(cached || {}), promise });

  try {
    return await promise;
  } catch (err) {
    binderStudyListCache.delete(key);
    throw err;
  }
}

export async function fetchEisfStudies({ organizationId, createdBy, force = false }) {
  const key = `${organizationId}:${createdBy}`;
  const cached = studyListCache.get(key);

  if (!force && cached?.studies) {
    return { studies: cached.studies, options: cached.options };
  }
  if (!force && cached?.promise) {
    return cached.promise;
  }

  const promise = (async () => {
    let list = [];
    try {
      list = await fetchEisfStudyListFast({ organizationId, createdBy });
    } catch (err) {
      console.warn("Fast eISF study list unavailable, using legacy endpoint.", err);
      list = await fetchEisfStudyListLegacy({ organizationId, createdBy });
    }

    const options = utils.mapToOptions(list, "StudyName", "StudyId");
    const result = { studies: list, options };
    studyListCache.set(key, { studies: list, options });
    return result;
  })();

  studyListCache.set(key, { ...(cached || {}), promise });

  try {
    return await promise;
  } catch (err) {
    studyListCache.delete(key);
    throw err;
  }
}

function pickTable(payload, ...keys) {
  for (const key of keys) {
    const val = payload?.[key];
    if (Array.isArray(val)) return val;
  }
  return [];
}

export function normalizeUnresolvedComments(rows = []) {
  return rows.map((r, i) => ({
    id: r.Id ?? r.CommentId ?? r.id ?? i,
    documentTitle: r.DocumentTitle ?? r.documentTitle ?? r.FileName ?? "—",
    commenter: r.Commenter ?? r.CommenterName ?? r.StaffName ?? r.UserName ?? "—",
    preview: r.Message ?? r.Comment ?? r.CommentText ?? r.preview ?? "",
    priority: r.Priority ?? r.priority ?? "STANDARD",
  }));
}

export function normalizeRecentActivity(rows = []) {
  return rows.map((r, i) => ({
    id: r.Id ?? r.ActivityId ?? r.id ?? i,
    doneBy: r.DoneBy ?? r.StaffName ?? r.UserName ?? r.CreatedByName ?? "—",
    documentTitle: r.DocumentTitle ?? r.FileName ?? r.documentTitle ?? "—",
    action: r.Activity ?? r.Action ?? r.ActionType ?? "—",
    reason: r.Reason ?? r.Description ?? r.Comments ?? r.Comment ?? "",
  }));
}

export async function fetchLegacyDashboard({
  organizationId,
  createdBy,
  studyId = "",
}) {
  const response = await apiClient.post(API_ENDPOINTS.ISF.GET_DASHBOARD_DATA, {
    OrganizationId: organizationId,
    ActionType: "GetDashboard",
    CreatedBy: createdBy,
    StudyId: studyId || "",
  });

  const payload = response?.data ?? response ?? {};
  const counts = pickTable(payload, "CountTable", "countTable")[0] ?? {};

  return {
    folders: Number(counts.Folders ?? 0),
    documents: Number(counts.Documents ?? 0),
    lockedDocuments: Number(counts.LockedDocuments ?? 0),
    signedDocuments: Number(counts.SignedDocuments ?? 0),
    memoryUsed: Number(counts.MemoryUsed ?? 0),
    unresolvedComments: normalizeUnresolvedComments(
      pickTable(payload, "UnresolvedComments", "unresolvedComments"),
    ),
    recentActivity: normalizeRecentActivity(
      pickTable(payload, "RecentActivity", "recentActivity"),
    ),
    documentCreationTrend: pickTable(payload, "DocumentCreationTrend", "documentCreationTrend"),
  };
}

export async function fetchStudyPortfolioRow({
  organizationId,
  createdBy,
  studyId,
  studyName,
  templateCategory,
}) {
  try {
    const [stats, readiness] = await Promise.all([
      fetchDashboardPlaceholderStats({
        organizationId,
        studyId,
        createdBy,
      }),
      fetchReadinessReport({
        organizationId,
        studyId,
        templateCategory,
        createdBy,
      }).catch(() => null),
    ]);

    const summary = readiness?.summary;
    return {
      studyId,
      studyName,
      pendingRequired: stats.pendingRequired,
      overdueCount: stats.overdueCount,
      readinessPercent:
        summary?.ReadinessPercent != null ? Number(summary.ReadinessPercent) : null,
      requiredTotal: summary?.RequiredTotal != null ? Number(summary.RequiredTotal) : null,
    };
  } catch {
    return {
      studyId,
      studyName,
      pendingRequired: 0,
      overdueCount: 0,
      readinessPercent: null,
      requiredTotal: null,
    };
  }
}

export async function fetchStudyPortfolio({
  organizationId,
  createdBy,
  studies = [],
  templateCategory,
  maxStudies = 25,
}) {
  const slice = studies.slice(0, maxStudies);
  const rows = await Promise.all(
    slice.map((s) =>
      fetchStudyPortfolioRow({
        organizationId,
        createdBy,
        studyId: s.StudyId,
        studyName: s.StudyName,
        templateCategory,
      }),
    ),
  );
  return rows.sort((a, b) => {
    if (b.overdueCount !== a.overdueCount) return b.overdueCount - a.overdueCount;
    if (b.pendingRequired !== a.pendingRequired) return b.pendingRequired - a.pendingRequired;
    return String(a.studyName).localeCompare(String(b.studyName), undefined, {
      sensitivity: "base",
    });
  });
}

export async function fetchStudyOperationalData({
  organizationId,
  createdBy,
  studyId,
  templateCategory,
  gateCodes = [],
  gateCode = null,
}) {
  const codes =
    gateCodes?.length > 0
      ? gateCodes
      : gateCode
        ? [gateCode]
        : [];

  const [readiness, placeholders, folderBundle] = await Promise.all([
    fetchReadinessReport({
      organizationId,
      studyId,
      templateCategory,
      createdBy,
    }),
    fetchStudyPlaceholdersForGates({
      organizationId,
      studyId,
      templateCategory,
      createdBy,
      gateCodes: codes,
    }).catch(() => []),
    fetchStudyFolderBundle({
      organizationId,
      staffId: createdBy,
      studyId,
      templateCategory,
    }).catch(() => ({ staffList: [], documents: [] })),
  ]);

  const scopedReadiness =
    codes.length > 0
      ? filterReadinessReportByGate(readiness, placeholders)
      : readiness;

  const documents = enrichDocumentsWithPlaceholderLinks(
    folderBundle.documents,
    placeholders,
  );

  return {
    readiness: scopedReadiness,
    placeholders,
    documents,
    staffList: folderBundle.staffList,
    rawReadiness: readiness,
  };
}

function mapStaffListRecords(records = []) {
  return records.map((item) => ({
    value: item.id,
    label: item.name,
    email: item.EmailId,
    Role: item.Role,
    roleId: item.RoleId || item.roleId,
  }));
}

/** @deprecated Use consolidateDocumentSignatureRows from isfPlaceholderService */
export function consolidateFolderDocumentsForDashboard(rows = [], staffList = []) {
  return consolidateDocumentSignatureRows(rows, staffList);
}

export async function fetchStudyFolderBundle({
  organizationId,
  staffId,
  studyId,
  templateCategory,
}) {
  const response = await apiClient.post(API_ENDPOINTS.ISF.GET_STUDY_FOLDERS, {
    OrganizationIdRef: organizationId,
    ActionType: "GetAllFolder",
    StaudyIdRef: studyId,
    StaffIdRef: staffId,
    TemplateId: "",
    TemplateName: "",
    TemplateCategory: templateCategory || "",
    SiteIdRef: null,
  });

  const data = normalizeStudyFolderResponseData(response?.data ?? {});
  const staffList = mapStaffListRecords(
    Array.isArray(data.StaffListRecord) ? data.StaffListRecord : [],
  );
  const rawDocs = Array.isArray(data.FolderDocuments) ? data.FolderDocuments : [];
  const documents = consolidateDocumentSignatureRows(rawDocs, staffList);
  return { staffList, documents };
}

async function mapStudiesInBatches(items, batchSize, mapper) {
  const results = [];
  for (let index = 0; index < items.length; index += batchSize) {
    const batch = items.slice(index, index + batchSize);
    const batchResults = await Promise.all(batch.map(mapper));
    results.push(...batchResults);
  }
  return results;
}

function buildSiteNameLookup(siteList = []) {
  const map = new Map();
  (siteList || []).forEach((site) => {
    const id = String(site?.SiteId || site?.value || site?.siteId || "")
      .replace(/[{}]/g, "")
      .trim()
      .toLowerCase();
    const name = String(site?.SiteName || site?.label || site?.siteName || "").trim();
    if (id && name) map.set(id, name);
  });
  return map;
}

async function loadStudyTaskBucket({
  organizationId,
  createdBy,
  study,
  templateCategory,
}) {
  try {
    const [placeholders, folderBundle] = await Promise.all([
      fetchStudyPlaceholders({
        organizationId,
        studyId: study.StudyId,
        templateCategory,
        createdBy,
      }),
      fetchStudyFolderBundle({
        organizationId,
        staffId: createdBy,
        studyId: study.StudyId,
        templateCategory,
      }).catch(() => ({ staffList: [], documents: [] })),
    ]);

    const documents = enrichDocumentsWithPlaceholderLinks(
      folderBundle.documents,
      placeholders,
    );

    return {
      studyId: study.StudyId,
      studyName: study.StudyName,
      studyNumber: study.StudyNumber || "",
      studyColor: study.StudySchColor || study.StudyColor || "",
      placeholders,
      documents,
      staffList: folderBundle.staffList,
    };
  } catch {
    return {
      studyId: study.StudyId,
      studyName: study.StudyName,
      studyNumber: study.StudyNumber || "",
      studyColor: study.StudySchColor || study.StudyColor || "",
      placeholders: [],
      documents: [],
      staffList: [],
    };
  }
}

export async function fetchDashboardTaskQueue({
  organizationId,
  createdBy,
  userData = null,
  studies = [],
  studyIdFilter = "",
  templateCategory = null,
  maxStudies = 10,
  batchSize = 3,
}) {
  const targetStudies = studyIdFilter
    ? studies.filter((s) => String(s.StudyId) === String(studyIdFilter))
    : studies.slice(0, maxStudies);

  if (!targetStudies.length) {
    return buildDashboardTaskQueue({
      studyBuckets: [],
      userData: userData || { StaffId: createdBy, OrganizationId: organizationId },
      roleOptions: [],
      siteLookup: new Map(),
    });
  }

  let roleOptions = [];
  let siteLookup = new Map();
  const lookupUser = userData || { OrganizationId: organizationId, StaffId: createdBy };

  try {
    const [roles, sites] = await Promise.all([
      fetchRoleLookup(lookupUser),
      fetchSiteLookup(lookupUser).catch(() => []),
    ]);
    roleOptions = roles;
    siteLookup = buildSiteNameLookup(sites);
  } catch {
    roleOptions = [];
  }

  const buckets = await mapStudiesInBatches(targetStudies, batchSize, (study) =>
    loadStudyTaskBucket({
      organizationId,
      createdBy,
      study,
      templateCategory,
    }),
  );

  return buildDashboardTaskQueue({
    studyBuckets: buckets,
    userData: userData || { StaffId: createdBy, OrganizationId: organizationId },
    roleOptions,
    siteLookup,
  });
}
